import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spin, Typography, Button } from 'antd';
import SharedTransfer from 'src/components/Transfer';

import { getProjects } from 'src/redux/features/session/slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  assignProject,
  fetchAssignedProjectsOfResource,
  fetchProjects,
} from 'src/redux/features/projects/projectsSlice';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import { getTimesheetByUser } from 'src/redux/features/timesheet/slice';
import dayjs from 'dayjs';
import { RecordType } from 'src/models/EditProject';
import en from 'src/messages/en.json';

const EditProjectModal = ({ show, onHide, weekDate }) => {
  const [data, setData] = useState<RecordType[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [resourceProjects, setResourceProjects] = useState<string[]>([]);
  const [originalTargetKeys, setOriginalTargetKeys] = useState<string[]>([]);
  const [lastMovedKeys, setLastMovedKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [projects, setProjects] = useState<any>();

  const { Title } = Typography;
  const dispatch = useDispatch();
  const auth = useAuth();

  const handleSave = async () => {
    setSaveLoading(true);
    const intProjectIds = targetKeys.map((key) => parseInt(key, 10));
    const payload = intProjectIds.map((id) => ({ projectId: id }));

    try {
      const result = await dispatch(
        assignProject({
          payload,
          date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
        }),
      ).unwrap();

      if (result === true) {
        handleCancel();
        toast.success(en.Common.Success.changesUpdated);
        dispatch(
          getTimesheetByUser({
            resource: auth?.user?.profile?.email,
            date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
          }),
        );
      }
    } catch (error) {
      await fetchData();
    } finally {
      setSaveLoading(false);
      setIsSaveDisabled(true);
    }
  };
  const handleChange = (
    nextTargetKeys: string[],
    direction: string,
    moveKeys: React.Key[],
  ) => {
    setTargetKeys(nextTargetKeys);
    setLastMovedKeys(moveKeys);
    const isChanged =
      JSON.stringify([...nextTargetKeys].sort()) !==
      JSON.stringify([...originalTargetKeys].sort());

    setIsSaveDisabled(!isChanged);

    if (direction === 'left') {
      setData((prev) => {
        const movedItems = prev.filter((item) => moveKeys.includes(item.key));
        const remainingItems = prev.filter(
          (item) => !moveKeys.includes(item.key),
        );

        return [...movedItems, ...remainingItems];
      });
    }
  };

  const handleSelectChange = (source: string[], target: string[]) => {
    setSelectedKeys([...source, ...target]);
  };

  const filterOption = useMemo(
    () => (input: string, option: RecordType) =>
      option.description.toLowerCase().includes(input.toLowerCase()),
    [],
  );

  const handleSearch = (direction: 'left' | 'right', value: string) => {
    // intentionally left blank
  };

  const handleCancel = () => {
    onHide();
    setLastMovedKeys([]);
    setSelectedKeys([]);
    setTargetKeys([]);
    setIsSaveDisabled(true);
  };
  const fetchData = async () => {
    try {
      setLoading(true);
      if (show && auth?.user?.profile?.email) {
        const res = await dispatch(
          fetchProjects({
            date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
          }),
        ).unwrap();

        setProjects(res);
        const response = await dispatch(
          fetchAssignedProjectsOfResource({
            resource: auth.user.profile.email,
            date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
          }),
        );
        const data = response.payload;

        setResourceProjects(data.map((item) => item.projectName));
      }
    } catch (error) {
      // Handle error if needed
    }
  };

  useEffect(() => {
    fetchData();
  }, [show, auth?.user?.profile?.email]);

  useEffect(() => {
    const mock: RecordType[] = projects
      ?.map((project) => ({
        key: project.id.toString(),
        title: project.name,
        description: `Project: ${project.name}`,
        disabled: false,
        isDefault: project.isDefault,
      }))
      .filter((project) => !project.isDefault);

    setData(mock);

    const initialKeys = mock
      ?.filter((item) => resourceProjects.includes(item.title))
      .map((item) => item.key);

    setTargetKeys(initialKeys);
    setOriginalTargetKeys(initialKeys);
    setLoading(false);
  }, [projects, resourceProjects]);

  return (
    <Modal
      className="no-padding-modal"
      open={show}
      onCancel={handleCancel}
      footer={null}
      width={'50rem'}
      centered
      maskClosable={false}
      style={{ padding: 0 }}
      bodyStyle={{
        padding: '1rem',
        height: 500,
      }}
      zIndex={1200}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 1rem 0rem',
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            {en.Timesheet.Title.editProjects}
          </Title>
        </div>
      }
    >
      <div style={{ height: '90%', position: 'relative' }}>
        {loading ? (
          <Spin
            size="default"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
            }}
          />
        ) : (
          <SharedTransfer
            style={{ height: '100%' }}
            titles={[
              en.Timesheet.Title.availableProjects,
              en.Timesheet.Title.addedProjects,
            ]}
            dataSource={data}
            targetKeys={targetKeys}
            selectedKeys={selectedKeys}
            onChange={handleChange}
            onSelectChange={handleSelectChange}
            filterOption={filterOption}
            onSearch={handleSearch}
            showSearch
            disabled={disabled}
            lastMovedKeys={lastMovedKeys}
          />
        )}
      </div>

      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '0rem',
        }}
      >
        <Button
          type="primary"
          onClick={handleSave}
          loading={saveLoading}
          disabled={isSaveDisabled}
        >
          {'Save'}
        </Button>
      </div>
    </Modal>
  );
};
export default EditProjectModal;
